import { supabase, supabaseAnonKey, supabaseFunctionsUrl } from '../supabase'
import { isAuthDebugEnabled } from '../debug'

function getAuthenticatedFunctionsBaseUrl(): string {
  const base = supabaseFunctionsUrl?.replace(/\/$/, '')
  if (base) return base
  throw new Error('Supabase functions URL not configured')
}

export type InvokeOptions = {
  method?: string
  headers?: Record<string, string>
  body?: any
  params?: Record<string, string | number | boolean>
  quietFetchFailure?: boolean
  signal?: AbortSignal
}

export async function invokeWithAuth<T = unknown>(path: string, options: InvokeOptions = {}) {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession()

  if (error) {
    console.error('[invokeWithAuth] Session error:', error)
    return { data: null, error }
  }

  if (!session?.access_token) {
    // In-memory session may be null if the token expired between page load and this call
    // (e.g. tab was backgrounded, auto-refresh failed). Try an explicit refresh before giving up.
    console.log('[invokeWithAuth] No in-memory session — attempting session refresh')
    const {
      data: { session: refreshedSession },
      error: refreshError,
    } = await supabase.auth.refreshSession()

    if (refreshError || !refreshedSession?.access_token) {
      console.error(
        '[invokeWithAuth] Session refresh failed — user must sign in again:',
        refreshError?.message,
      )
      return { data: null, error: new Error('Your session has expired. Please sign in again.') }
    }

    console.log('[invokeWithAuth] Session recovered via refresh — retrying')
    return invokeWithAuth<T>(path, options)
  }

  if (session.expires_at && Date.now() / 1000 > session.expires_at) {
    console.log('[invokeWithAuth] Token expired, refreshing session...')
    const {
      data: { session: newSession },
      error: refreshError,
    } = await supabase.auth.refreshSession()

    if (refreshError || !newSession?.access_token) {
      console.error('[invokeWithAuth] Session refresh failed:', refreshError)

      if (
        refreshError?.message?.includes('Invalid Refresh Token') ||
        refreshError?.message?.includes('Refresh Token Not Found')
      ) {
        console.log('[invokeWithAuth] Invalid refresh token detected, signing out...')
        await supabase.auth.signOut()
        throw new Error('Session expired. Please sign in again.')
      }

      throw new Error('Session expired and refresh failed')
    }

    console.log('[invokeWithAuth] Session refreshed successfully')
    return invokeWithAuth<T>(path, options)
  }

  if (isAuthDebugEnabled()) {
    console.log('[invokeWithAuth] Session found, calling with Authorization header')
  }

  const method = options.method?.toUpperCase() ?? 'POST'
  const headers: Record<string, string> = {
    ...(options.headers ?? {}),
    Authorization: `Bearer ${session.access_token}`,
    ...(supabaseAnonKey ? { apikey: supabaseAnonKey } : {}),
  }

  let body: BodyInit | undefined
  if (options.body !== undefined && method !== 'GET' && method !== 'HEAD') {
    if (
      options.body instanceof FormData ||
      options.body instanceof URLSearchParams ||
      typeof options.body === 'string'
    ) {
      body = options.body
    } else if (options.body instanceof Blob) {
      body = options.body
    } else {
      body = JSON.stringify(options.body)
      if (!headers['Content-Type']) {
        headers['Content-Type'] = 'application/json'
      }
    }
  }

  const functionsBaseUrl = getAuthenticatedFunctionsBaseUrl()
  const url = new URL(`${functionsBaseUrl}/${path}`)
  if (options.params) {
    Object.entries(options.params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, String(value))
      }
    })
  }

  if (isAuthDebugEnabled()) {
    console.log('[invokeWithAuth] Calling', method, path)
  }

  let response: Response
  try {
    response = await fetch(url.toString(), { method, headers, body, signal: options.signal })
    if (isAuthDebugEnabled()) {
      console.log('[invokeWithAuth] Response status:', response.status)
    }
  } catch (fetchError) {
    if (!options.quietFetchFailure) {
      console.error('[invokeWithAuth] Fetch failed:', {
        error: fetchError,
        url: url.toString(),
        method,
        message: fetchError instanceof Error ? fetchError.message : 'Unknown error',
      })
    } else if (isAuthDebugEnabled()) {
      console.debug('[invokeWithAuth] Fetch failed (quiet):', path, fetchError)
    }
    throw fetchError
  }

  let data: T | null = null
  try {
    const contentType = response.headers.get('Content-Type') || ''
    if (contentType.includes('application/json')) {
      data = await response.json()
      if (isAuthDebugEnabled()) console.log('[invokeWithAuth] Response data received')
    } else {
      const text = await response.text()
      if (isAuthDebugEnabled()) console.log('[invokeWithAuth] Response text received')
      data = text as unknown as T
    }
  } catch (parseError) {
    console.error('[invokeWithAuth] Failed to parse response:', parseError)
    data = null
  }

  if (!response.ok) {
    const errorMsg =
      typeof data === 'object' && data && 'error' in (data as any)
        ? ((data as any).error as string)
        : response.statusText
    const errorDetails =
      typeof data === 'object' && data && 'error_details' in (data as any)
        ? ((data as any).error_details as string)
        : undefined
    const errorType =
      typeof data === 'object' && data && 'error_type' in (data as any)
        ? ((data as any).error_type as string)
        : undefined
    const message =
      typeof data === 'object' && data && 'message' in (data as any)
        ? ((data as any).message as string)
        : undefined
    const details =
      typeof data === 'object' && data && 'details' in (data as any)
        ? ((data as any).details as string)
        : undefined
    const code =
      typeof data === 'object' && data && 'code' in (data as any)
        ? String((data as any).code)
        : undefined

    // Expected probe while Lens v7 resume waits for summarize — not a hard failure.
    const isAssessmentNotCompleted =
      (response.status === 409 && code === 'ASSESSMENT_NOT_COMPLETED') ||
      code === 'ASSESSMENT_IN_PROGRESS'

    if (isAssessmentNotCompleted) {
      console.debug(
        `[invokeWithAuth] Assessment not completed yet: path=${path} status=${response.status}`,
      )
    } else {
      console.error(
        `[invokeWithAuth] API request failed: path=${path} status=${response.status} errorMsg=${errorMsg} message=${message} details=${details} errorDetails=${errorDetails} errorType=${errorType}`,
      )
      console.error('[invokeWithAuth] Full response data:', JSON.stringify(data))
    }

    const summary = [errorMsg, message, details, errorDetails]
      .filter((part) => typeof part === 'string' && part.trim().length > 0)
      .join(' — ')
    const statusPrefix = response.status ? `HTTP ${response.status}` : ''
    const combined = [statusPrefix, summary || response.statusText || 'API request failed']
      .filter(Boolean)
      .join(': ')

    return {
      data: data as T,
      error: new Error(combined),
    }
  }

  return { data: data as T, error: null }
}
